import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { Box, Flex, Heading, Image, Text } from "@highoutput/ui-components";
import MarkdownIt from "markdown-it";
import Link from "next/link";
import { useEffect, useState } from "react";
import { InView } from "react-intersection-observer";
import type { PostsQuery } from "~/__generated__/graphql";
import takeFirstParagraph from "~/utils/take-first-paragraph";

type BlogProps = {
  data: NonNullable<PostsQuery["posts"]>["data"];
};

export default function Blog({ data }: BlogProps) {
  const [contentPreview, setContentPreview] = useState("");

  useEffect(() => {
    setContentPreview(md.render(takeFirstParagraph(data.at(0)?.attributes?.content)));

    return () => setContentPreview("");
  }, [data]);

  if (!data.length) return null;

  return (
    <Box
      w={{ xl: "85%", lg: "95%", base: "90%" }}
      mx="auto"
      zIndex={1}
      bg="white"
      py={{
        base: "64px",
        lg: "100px",
        xl: "100px",
      }}
      fontFamily="'Inter', sans-serif"
      bgColor={"white"}
    >
      <Flex direction={"column"} align={"center"} mb={{ lg: "64px", base: "40px" }}>
        <Text
          fontSize={{ xl: "18px", lg: "18px", base: "14px" }}
          lineHeight={{ xl: "18px", lg: "18px", base: "14px" }}
          letterSpacing={"0.05em"}
          color="rgba(13, 48, 80, 0.8)"
          mb="16px"
        >
          HAVE A READ
        </Text>
        <Text
          fontFamily={"Georgia"}
          color="#0D3050"
          fontSize={{ xl: "40px", lg: "40px", base: "32px" }}
          lineHeight={{ xl: "44px", lg: "44px", base: "36px" }}
          letterSpacing={"-0.02em"}
          textAlign={"center"}
        >
          Our latest blog articles
        </Text>
      </Flex>
      <InView triggerOnce>
        {({ ref, inView }) => (
          <Flex
            ref={ref}
            direction={{
              base: "column",
              lg: "row",
            }}
            alignItems="center"
            gap={{
              base: "24px",
              lg: "24px",
            }}
            justify={"center"}
            sx={{
              opacity: 0,
              transform: "translateY(10vh)",
              transition: "transform 600ms ease-out, opacity 600ms ease-out",
              willChange: "transform, opacity",
              ...(inView && {
                opacity: 1,
                transform: "none",
              }),
            }}
            mx="auto"
          >
            {data.map((d) => {
              return (
                <Flex
                  direction={"column"}
                  key={d.id}
                  maxWidth={{ xl: "352px", lg: "309px" }}
                  align={"center"}
                  height={"404.67px"}
                  bgColor={"rgba(240, 237, 236, 0.4)"}
                  pb="24px"
                  position="relative"
                >
                  <Image
                    alt=""
                    src={d.attributes?.cover.data?.attributes?.url}
                    data-testid={`post.${d.id}.image`}
                    width={{ xl: "352px", lg: "309px", base: "343px" }}
                    rounded="4px"
                    height={{ xl: "185px", lg: "173.13px", base: "184.38px" }}
                    objectFit={"fill"}
                    mb={{ lg: "24px", base: "23.75px" }}
                  />

                  <Box maxWidth={{ xl: "304px", lg: "261px" }} px={{ lg: "unset", base: "24px" }}>
                    <Heading
                      fontSize={"20px"}
                      lineHeight={"24px"}
                      letterSpacing={{
                        base: "-0.012em",
                        lg: "-0.015em",
                      }}
                      fontFamily={"Georgia"}
                      overflow="hidden"
                      data-testid={`post.${data[0].id}.title`}
                      fontWeight={400}
                      noOfLines={2}
                      color="#0D3050"
                    >
                      {d.attributes?.title}
                    </Heading>

                    <Text
                      color="rgba(13, 48, 80, 0.8)"
                      fontSize={"16px"}
                      lineHeight={"24px"}
                      mt={{ lg: "12px", base: "7.87px" }}
                      letterSpacing={"0.02em"}
                      maxHeight={{
                        base: "84px",
                        lg: "48px",
                      }}
                      noOfLines={2}
                      overflow="hidden"
                      sx={{
                        "& > *": {
                          display: "inline",
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: contentPreview,
                      }}
                      data-testid={`post.${d.id}.description`}
                      mb={{ lg: "48px", base: "24.13px" }}
                    />
                  </Box>
                  <Flex
                    as={Link}
                    role="navigation"
                    align={"center"}
                    gap={"12px"}
                    textAlign={"left"}
                    aria-label={`Read more about ${d.attributes?.slug}`}
                    href={`/blog/${d.attributes?.slug}`}
                    width="fit-content"
                    color="rgba(13, 48, 80, 0.8)"
                    bg="transparent"
                    fontSize={"18px"}
                    lineHeight={"24px"}
                    fontWeight="medium"
                    borderBottom="1px solid transparent"
                    _hover={{
                      borderBottom: "1px solid #0D3050",
                    }}
                    data-testid={`post.${d.id}.link`}
                    w="fit-content"
                    position="absolute"
                    bottom="24px"
                    left="24px"
                  >
                    <Text as="span"> Know more </Text>{" "}
                    <Text as="span">
                      <ArrowRightIcon width="20px" height="20px" />{" "}
                    </Text>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        )}
      </InView>
    </Box>
  );
}

const md = new MarkdownIt({
  html: true,
});
